<template>
  <BittsCard
    v-if="isSessionPolicyAvailable"
    title-text="Session Timeout"
    class="session-timeout-card"
  >
    <div class="session-timeout-card__content">
      <SessionTimeoutDurationField
        v-model="draft.session_max_duration_secs"
        v-model:has-invalid-amount="hasInvalidAmount.maxDuration"
        name="session-max-duration"
        label="Maximum session length"
        help-text="How long someone may stay signed in before they have to sign in again, even while active."
        :default-secs="limits.max_duration_secs.max"
        :error-message="maxDurationErrorMessage"
        :is-disabled="!canEditSessionPolicy"
      />
      <SessionTimeoutDurationField
        v-model="draft.session_idle_timeout_secs"
        v-model:has-invalid-amount="hasInvalidAmount.idleTimeout"
        name="session-idle-timeout"
        label="Idle timeout"
        help-text="How long a session may sit inactive before it is signed out."
        :default-secs="effectiveIdleDefaultSecs"
        :error-message="idleTimeoutErrorMessage"
        :is-disabled="!canEditSessionPolicy"
      />
      <p class="session-timeout-card__hint">
        {{
          canEditSessionPolicy
            ? "Leave a field empty to use Crossbeam's default."
            : 'Only organization admins can change these settings.'
        }}
      </p>
      <BittsButton
        v-if="canEditSessionPolicy"
        text="Save Changes"
        size="small"
        class="self-start"
        :loading="isSaving"
        :disabled="isSaveDisabled"
        data-testid="save-session-timeout-button"
        @click="onSaveSessionPolicy"
      />
    </div>
  </BittsCard>
</template>

<script setup lang="ts">
import { BittsButton, BittsCard } from '@crossbeam/bitts';
import type { GetApiResponseByUrl } from '@crossbeam/openapi';

import { computed, onMounted, reactive, ref } from 'vue';

import SessionTimeoutDurationField from '@/components/SessionTimeoutDurationField.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import { formatDuration } from '@/utils/sessionDuration';

type SessionPolicyResponseType =
  GetApiResponseByUrl<'/v0.1/organization/settings/session-policy'>;
type SessionPolicyLimitsType = SessionPolicyResponseType['limits'];
type SessionPolicyLimitType = SessionPolicyLimitsType['max_duration_secs'];

const DEFAULT_LIMIT: SessionPolicyLimitType = { min: 0, max: 0 };

const flashesStore = useFlashesStore();

const isSessionPolicyAvailable = ref(false);

const { hasScope } = useAuth();
const canEditSessionPolicy = computed(() => hasScope('write:organization'));
const isSaving = ref(false);

const savedPolicy = ref<SessionPolicyResponseType | null>(null);
const draft = reactive({
  session_max_duration_secs: null as number | null,
  session_idle_timeout_secs: null as number | null,
});
const hasInvalidAmount = reactive({ maxDuration: false, idleTimeout: false });
const limits = reactive<SessionPolicyLimitsType>({
  max_duration_secs: DEFAULT_LIMIT,
  idle_timeout_secs: DEFAULT_LIMIT,
});

function rangeErrorMessage(
  secs: number | null,
  limit: SessionPolicyLimitType,
  hasInvalidAmount: boolean,
) {
  if (hasInvalidAmount) return 'Enter a whole number greater than zero.';
  if (secs === null) return '';
  if (secs < limit.min) return `Must be at least ${formatDuration(limit.min)}.`;
  if (secs > limit.max)
    return `Cannot be more than ${formatDuration(limit.max)}.`;
  return '';
}

const maxDurationErrorMessage = computed(() =>
  rangeErrorMessage(
    draft.session_max_duration_secs,
    limits.max_duration_secs,
    hasInvalidAmount.maxDuration,
  ),
);

const idleTimeoutErrorMessage = computed(() => {
  const rangeError = rangeErrorMessage(
    draft.session_idle_timeout_secs,
    limits.idle_timeout_secs,
    hasInvalidAmount.idleTimeout,
  );
  if (rangeError) return rangeError;
  if (
    draft.session_max_duration_secs &&
    draft.session_idle_timeout_secs &&
    draft.session_idle_timeout_secs > draft.session_max_duration_secs
  ) {
    return 'Idle timeout cannot be longer than the maximum session length.';
  }
  return '';
});

// The backend clamps the idle window to the maximum session length, so the
// placeholder has to advertise what would actually be enforced. Without this it
// reads "Default (8 days)" next to a one-hour maximum.
const effectiveIdleDefaultSecs = computed(() =>
  Math.min(
    limits.idle_timeout_secs.max,
    draft.session_max_duration_secs ?? limits.idle_timeout_secs.max,
  ),
);

const hasValidationError = computed(
  () => !!maxDurationErrorMessage.value || !!idleTimeoutErrorMessage.value,
);

const hasSettingChanged = computed(
  () =>
    draft.session_max_duration_secs !==
      savedPolicy.value?.session_max_duration_secs ||
    draft.session_idle_timeout_secs !==
      savedPolicy.value?.session_idle_timeout_secs,
);

const isSaveDisabled = computed(
  () => !hasSettingChanged.value || hasValidationError.value,
);

function applyPolicy(policy: SessionPolicyResponseType) {
  savedPolicy.value = policy;
  limits.max_duration_secs = policy.limits.max_duration_secs;
  limits.idle_timeout_secs = policy.limits.idle_timeout_secs;
  draft.session_max_duration_secs = policy.session_max_duration_secs;
  draft.session_idle_timeout_secs = policy.session_idle_timeout_secs;
}

async function onSaveSessionPolicy() {
  if (hasValidationError.value) return;
  isSaving.value = true;
  const { data, error } = await crossbeamApi.PUT(
    '/v0.1/organization/settings/session-policy',
    { body: { ...draft } },
  );
  if (error || !data) {
    flashesStore.addErrorFlash({ message: 'Failed to save session settings' });
    captureException(error);
  } else {
    flashesStore.addSuccessFlash({ message: 'Session settings saved' });
    applyPolicy(data);
  }
  isSaving.value = false;
}

onMounted(async () => {
  const { data, error, response } = await crossbeamApi.GET(
    '/v0.1/organization/settings/session-policy',
  );
  // Read the status before narrowing on `error`: the route declares only a
  // 200, so openapi-fetch types `response` as `never` inside the error branch.
  const status = response?.status;
  if (error) {
    if (status !== 403) {
      flashesStore.addErrorFlash({
        message: 'Failed to load session settings',
      });
      captureException(error);
    }
    return;
  }
  if (!data) return;
  applyPolicy(data);
  isSessionPolicyAvailable.value = true;
});
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";
.session-timeout-card__content {
  @apply px-16 pb-16 flex flex-col gap-16;
}
.session-timeout-card__hint {
  @apply text-neutral-text text-sm;
}
:deep(.bitts-card__title) {
  @apply text-lg text-neutral-text-strong font-bold;
}
</style>
